import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MdCalendarMonth,
  MdEdit,
  MdLock,
  MdContentCopy,
  MdCheck,
  MdOutlinePublic,
  MdPerson,
  MdVisibility,
  MdKey,
} from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from "../../Utils/Route";
import { getLoginCredsById, getAllLoginCreds, loadTitles } from "../../Services/LoginCredsService";
import { login as loginApi } from "../../Services/AuthenticationService";
import { formatUtcToLocalDateTime } from "../../Utils/DateTime";
import ReAuthModal from "../../Components/Common/ReAuthModal";
import ViewEmptyState from "../../Components/Common/ViewEmptyState";
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
  Active: { backgroundColor: "var(--hc-status-success-bg)", color: "var(--hc-status-success-text-strong)" },
  Inactive: { backgroundColor: "var(--hc-status-neutral-bg)", color: "var(--hc-table-footer-text)" },
  Pending: { backgroundColor: "var(--hc-status-pending-alt-bg)", color: "var(--hc-blue-700)" },
  Suspended: { backgroundColor: "var(--hc-status-pending-bg)", color: "var(--hc-status-pending-text)" },
  "Yet To Get": { backgroundColor: 'var(--hc-status-warning-bg)', color: 'var(--hc-status-warning-text)' },
};

const PASSWORD_VISIBLE_SECONDS = 10;
const MAX_REAUTH_ATTEMPTS = 2;

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const getField = (item, keys) => {
  if (!item) return undefined;
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }
  return undefined;
};

const normalizeLoginCred = (data) => ({
  id: getField(data, ["id", "Id", "login_cred_id", "LoginCredId"]),
  title: getField(data, ["title", "Title", "name", "Name"]),
  status_name: getField(data, ["status_name", "StatusName", "statusName"]),
  username: getField(data, ["username", "Username"]),
  password: getField(data, ["password", "Password"]),
  organization_name: getField(data, ["organization_name", "OrganizationName", "organizationName"]),
  provider_name: getField(data, ["provider_name", "ProviderName", "providerName"]),
  site_url: getField(data, ["site_url", "SiteUrl", "siteUrl"]),
  registered_url: getField(data, ["registered_url", "RegisteredUrl", "registration_url", "registrationUrl"]),
  phone: getField(data, ["phone", "Phone"]),
  email: getField(data, ["email", "Email"]),
  mfa_user: getField(data, ["mfa_user", "MfaUser", "mfaUser"]),
  created_by: getField(data, ["created_by", "CreatedBy", "createdBy"]),
  modified_by: getField(data, ["modified_by", "ModifiedBy", "modifiedBy"]),
  created_on: getField(data, ["created_on", "CreatedOn", "createdOn"]),
  modified_on: getField(data, ["modified_on", "ModifiedOn", "modifiedOn"]),
  backup_codes: normalizeArray(getField(data, ["backup_codes", "BackupCodes"])),
});

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const CountdownCircle = ({ secondsLeft, totalSeconds }) => {
  const size = 30;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const safeSeconds = Math.max(0, secondsLeft);
  const progress = totalSeconds > 0 ? safeSeconds / totalSeconds : 0;
  const offset = circumference * (1 - progress);
  const isWarning = safeSeconds < 5;
  const ringColor = isWarning ? "var(--hc-chart-5)" : "var(--hc-chart-8)";

  return (
    <div
      className="d-inline-flex align-items-center justify-content-center position-relative flex-shrink-0"
      style={{
        width: size,
        height: size,
      }}
      aria-label={`Password visible for ${secondsLeft} seconds`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--hc-status-unknown-bg)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset 1s linear, stroke 0.2s ease",
          }}
        />
      </svg>

      <div
        className="position-absolute d-flex align-items-center justify-content-center fs-6 fw-bold lh-1 user-select-none"
        style={{
          inset: 0,
          color: ringColor,
        }}
      >
        {safeSeconds}
      </div>
    </div>
  );
};

export default function LoginCredsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { token, permissions, decodedToken, login, logout } = useAuth();
  const userPermissionCodes = useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );
  const canViewLoginCred = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_VIEW, decodedToken);
  const canViewList = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_LIST, decodedToken);
  const canEditLoginCred = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_EDIT, decodedToken);

  const initialLoginCred = useMemo(
    () => (location.state?.loginCred ? normalizeLoginCred(location.state.loginCred) : null),
    [location.state],
  );

  const [loginCred, setLoginCred] = useState(initialLoginCred);
  const [loading, setLoading] = useState(!initialLoginCred);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [titleOptions, setTitleOptions] = useState([]);

  const [showPassword, setShowPassword] = useState(false);
  const [passwordCountdown, setPasswordCountdown] = useState(0);
  const [backupCodeIndex, setBackupCodeIndex] = useState(0);

  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthError, setReauthError] = useState("");
  const [reauthLoading, setReauthLoading] = useState(false);
  const [reauthAttempts, setReauthAttempts] = useState(0);
  const [showReauthPassword, setShowReauthPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [copiedUsername, setCopiedUsername] = useState(false);

  const targetCredId = useMemo(() => {
    if (!id || id === 'first' || id === ':id') return null;
    return id;
  }, [id]);

  const createdOnLocal = useMemo(
    () => formatUtcToLocalDateTime(loginCred?.CreatedOn ?? loginCred?.created_on),
    [loginCred?.CreatedOn, loginCred?.created_on],
  );

  const modifiedOnLocal = useMemo(
    () => formatUtcToLocalDateTime(loginCred?.ModifiedOn ?? loginCred?.modified_on),
    [loginCred?.ModifiedOn, loginCred?.modified_on],
  );

  const createdBy = useMemo(
    () => loginCred?.CreatedBy ?? loginCred?.created_by ?? "-",
    [loginCred?.CreatedBy, loginCred?.created_by],
  );

  const modifiedBy = useMemo(
    () => loginCred?.ModifiedBy ?? loginCred?.modified_by ?? "-",
    [loginCred?.ModifiedBy, loginCred?.modified_by],
  );

  const passwordTimerRef = useRef(null);

  const clearPasswordTimer = useCallback(() => {
    if (passwordTimerRef.current) {
      clearTimeout(passwordTimerRef.current);
      passwordTimerRef.current = null;
    }
  }, []);

  const resetPasswordReveal = useCallback(() => {
    clearPasswordTimer();
    setShowPassword(false);
    setPasswordCountdown(0);
  }, [clearPasswordTimer]);

  const loadLoginCred = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      let resolvedId = targetCredId;
      if (!resolvedId) {
        const listRes = await getAllLoginCreds(token);
        const rawList = listRes?.data?.login_creds || listRes?.data?.LoginCreds || listRes?.data || listRes;
        const list = Array.isArray(rawList) ? rawList : [];
        if (list.length > 0) {
          resolvedId = list[0]?.id ?? list[0]?.Id ?? list[0]?.login_cred_id;
        }
      }

      if (!resolvedId) {
        setLoginCred(null);
        return;
      }

      const response = await getLoginCredsById(resolvedId, token);
      if (response?.is_success) {
        setLoginCred(normalizeLoginCred(response.data ?? response));
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please contact support.");
      } else {
        toast.error(response?.message || "Failed to load login credential.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load login credential.");
    } finally {
      setLoading(false);
    }
  }, [targetCredId, token]);

  useEffect(() => {
    if (!token) return;

    if (!initialLoginCred) {
      loadLoginCred();
    }

    return () => {
      clearPasswordTimer();
    };
  }, [id, initialLoginCred, loadLoginCred, token, clearPasswordTimer]);

  const loadTitleOptions = useCallback(async () => {
    if (!token) return;

    try {
      const response = await loadTitles(token);
      const items = Array.isArray(response?.data) ? response.data : [];
      setTitleOptions(items);
    } catch (error) {
      console.error("Failed to load title options:", error);
      setTitleOptions([]);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadTitleOptions();
  }, [token, loadTitleOptions]);

  const ensureDetailsForPassword = useCallback(async () => {
    if (loginCred && loginCred.password != null && loginCred.password !== "") {
      return loginCred;
    }

    if (!id || !token) {
      throw new Error("Unable to fetch login credential details.");
    }

    setDetailsLoading(true);
    try {
      const response = await getLoginCredsById(id, token);
      if (response?.is_success) {
        const normalized = normalizeLoginCred(response.data ?? response);
        setLoginCred(normalized);
        return normalized;
      }
      throw new Error(response?.message || "Unable to fetch credential details.");
    } finally {
      setDetailsLoading(false);
    }
  }, [id, loginCred, token]);

  const handleShowPassword = async () => {
    if (showPassword) {
      resetPasswordReveal();
      return;
    }

    try {
      await ensureDetailsForPassword();
      setShowReAuthModal(true);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to verify password.");
    }
  };

  const handleReAuth = async () => {
    if (!reauthPassword.trim()) {
      setReauthError("Invalid Authentication.");
      return;
    }

    if (!decodedToken?.user_email) {
      setReauthError("Invalid Authentication.");
      return;
    }

    setReauthLoading(true);
    setReauthError("");

    try {
      const response = await loginApi({
        username: decodedToken.user_email,
        password: reauthPassword,
      });

      if (response?.is_success) {
        setShowReAuthModal(false);
        setReauthPassword("");
        setReauthAttempts(0);

        clearPasswordTimer();
        setPasswordCountdown(PASSWORD_VISIBLE_SECONDS);
        setShowPassword(true);
      } else {
        const nextAttempts = reauthAttempts + 1;
        setReauthAttempts(nextAttempts);

        if (nextAttempts >= MAX_REAUTH_ATTEMPTS) {
          toast.error("Maximum re-authentication attempts reached. Logging out.");
          logout();
          return;
        }

        setReauthError("Invalid Authentication.");
      }
    } catch (error) {
      console.error(error);

      const nextAttempts = reauthAttempts + 1;
      setReauthAttempts(nextAttempts);

      if (nextAttempts >= MAX_REAUTH_ATTEMPTS) {
        toast.error("Maximum re-authentication attempts reached. Logging out.");
        logout();
        return;
      }

      setReauthError("Invalid Authentication.");
    } finally {
      setReauthLoading(false);
    }
  };

  const passwordPlaceholder = useMemo(() => {
    if (!loginCred) return "-";
    if (showPassword) {
      return loginCred.password || "(Not available)";
    }
    return "••••••••";
  }, [loginCred, showPassword]);

  const matchedTitleOption = useMemo(() => {
    const title = loginCred?.title ?? loginCred?.Title ?? "";
    if (!title || !Array.isArray(titleOptions) || titleOptions.length === 0) {
      return null;
    }

    const normalizedTitle = String(title).trim().toLowerCase();
    return titleOptions.find((item) => {
      const itemName = item?.name ?? item?.Name ?? item?.title ?? "";
      return String(itemName).trim().toLowerCase() === normalizedTitle;
    }) ?? null;
  }, [loginCred?.title, loginCred?.Title, titleOptions]);

  const matchedSiteUrl =
    matchedTitleOption?.site_url ?? matchedTitleOption?.SiteUrl ?? matchedTitleOption?.siteUrl ?? null;
  const matchedRegisteredUrl =
    matchedTitleOption?.registration_url ??
    matchedTitleOption?.registrationUrl ??
    matchedTitleOption?.registered_url ??
    matchedTitleOption?.registeredUrl ??
    null;

  const backupCodes = useMemo(
    () => Array.isArray(loginCred?.backup_codes) ? loginCred.backup_codes : [],
    [loginCred?.backup_codes],
  );

  const hasBackupCodes = backupCodes.length > 0;
  const currentBackupCode = hasBackupCodes ? backupCodes[backupCodeIndex] : null;

  const nextBackupCode = useCallback(() => {
    if (!hasBackupCodes) return;
    setBackupCodeIndex((prev) => (prev + 1) % backupCodes.length);
  }, [backupCodes.length, hasBackupCodes]);

  const prevBackupCode = useCallback(() => {
    if (!hasBackupCodes) return;
    setBackupCodeIndex((prev) => (prev - 1 + backupCodes.length) % backupCodes.length);
  }, [backupCodes.length, hasBackupCodes]);

  useEffect(() => {
    if (backupCodes.length === 0) {
      setBackupCodeIndex(0);
      return;
    }
    if (backupCodeIndex >= backupCodes.length) {
      setBackupCodeIndex(backupCodes.length - 1);
    }
  }, [backupCodes.length, backupCodeIndex]);

  useEffect(() => {
    if (!showPassword) {
      return;
    }

    if (passwordCountdown <= 0) {
      clearPasswordTimer();
      setShowPassword(false);
      setPasswordCountdown(0);
      return;
    }

    passwordTimerRef.current = window.setTimeout(() => {
      setPasswordCountdown((previous) => Math.max(previous - 1, 0));
    }, 1000);

    return () => {
      clearPasswordTimer();
    };
  }, [passwordCountdown, showPassword, clearPasswordTimer]);

  useEffect(() => {
    if (!showPassword || passwordCountdown <= 0) {
      setCopiedPassword(false);
    }
  }, [showPassword, passwordCountdown]);

  useEffect(() => {
    setCopiedUsername(false);
  }, [loginCred?.username]);

  const handleCopyPassword = async () => {
    try {
      const text = loginCred?.password ?? "";
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Failed to copy');
    }
  };

  const handleCopyUsername = async () => {
    try {
      const text = loginCred?.username ?? "";
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopiedUsername(true);
      setTimeout(() => setCopiedUsername(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Failed to copy');
    }
  };

  const renderCredentialItemValue = (item) => {
    if (item.isPassword) {
      return (
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div className="fw-semibold" style={{ wordBreak: "break-all" }}>
            {item.value}
          </div>
          {!showPassword ? (
            <button
              type="button"
              className="btn btn-sm p-0 text-primary d-inline-flex align-items-center justify-content-center border-0"
              style={{ minWidth: 0, backgroundColor: "transparent" }}
              onClick={handleShowPassword}
              disabled={detailsLoading || reauthLoading}
              title="View password"
              aria-label="View password"
            >
              {detailsLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <MdVisibility size={18} />
              )}
            </button>
          ) : (
            <div className="d-flex align-items-center gap-2">
              {passwordCountdown > 0 && (
                <button
                  type="button"
                  className="btn btn-sm p-0 mx-1 text-primary d-inline-flex align-items-center justify-content-center border-0"
                  style={{ minWidth: 0, backgroundColor: 'transparent' }}
                  onClick={handleCopyPassword}
                  title={copiedPassword ? 'Copied' : 'Copy password'}
                  aria-label="Copy password"
                >
                  {copiedPassword ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
                </button>
              )}
              <CountdownCircle
                secondsLeft={passwordCountdown}
                totalSeconds={PASSWORD_VISIBLE_SECONDS}
              />
            </div>
          )}
        </div>
      );
    }

    if (item.label === 'Username') {
      return (
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="fw-semibold text-truncate" style={{ maxWidth: 'calc(100% - 36px)' }}>
            {item.value}
          </div>
          <button
            type="button"
            className="btn btn-sm p-0 text-primary d-inline-flex align-items-center justify-content-center border-0"
            style={{ minWidth: 0, backgroundColor: 'transparent' }}
            onClick={handleCopyUsername}
            title={copiedUsername ? 'Copied' : 'Copy username'}
            aria-label="Copy username"
          >
            {copiedUsername ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
          </button>
        </div>
      );
    }

    return <div className="fw-semibold">{item.value}</div>;
  };

  const statusName = loginCred?.status_name || "Unknown";

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 110, borderRadius: 999 }} />
          <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 10 }} />
        </div>

        <div className="p-3 p-lg-4 mb-4 hc-surface overflow-hidden">
          <div className="row g-4 g-xl-5 align-items-stretch">
            <div className="col-xl-9">
              <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 124, height: 124, borderRadius: 28 }} />
                <div className="flex-grow-1 w-100">
                  <div className="d-flex gap-2 mb-3">
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: 86, borderRadius: 8 }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: 64, borderRadius: 8 }} />
                  </div>
                  <div className="skeleton mb-2" style={{ height: 38, width: "72%", borderRadius: 10 }} />
                  <div className="skeleton mb-1 rounded-3" style={{ height: 16, width: "86%" }} />
                  <div className="skeleton mb-3 rounded-3" style={{ height: 16, width: "58%" }} />
                  <div className="skeleton mb-2 rounded-3" style={{ height: 20, width: 110 }} />
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 86, borderRadius: 16 }} />
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 86, borderRadius: 16 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3">
              <div className="skeleton h-100 rounded-4" style={{ minHeight: 220 }} />
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 200, borderRadius: 20 }} />
          </div>
          <div className="col-12 col-lg-4">
            <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 200, borderRadius: 20 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!loginCred) {
    return (
      <ViewEmptyState
        title="No Login Credentials Available"
        description="There are no login credential records available to display."
        icon={MdKey}
        canViewList={canViewList}
        backRoute={APPRoutes.LOGIN_CREDS}
        backLabel="Back to Credentials"
      />
    );
  }

  if (!canViewLoginCred) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to view this login credential.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        {canViewList && (
          <BackButton onClick={() => navigate(APPRoutes.LOGIN_CREDS)} />
        )}

        {canEditLoginCred ? (
          <button
            type="button"
            className="btn hc-btn-next border-0 d-inline-flex align-items-center gap-2"
            onClick={() => navigate(APPRoutes.LOGIN_CREDS_EDIT.replace(":id", String(loginCred.id || id)))}
          >
            <MdEdit size={18} />
            <span>Edit Credential</span>
          </button>
        ) : null}
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary rounded-4">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <span
                className="px-3 py-2 rounded-pill fw-semibold"
                style={{
                  fontSize: "0.82rem",
                  backgroundColor: STATUS_STYLE[statusName]?.backgroundColor || "var(--hc-status-neutral-bg)",
                  color: STATUS_STYLE[statusName]?.color || "var(--hc-table-footer-text)",
                }}
              >
                {statusName}
              </span>
            </div>

            <h2 className="fw-bold text-white mb-2" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.2rem)" }}>
              {formatValue(loginCred.title)}
            </h2>
            <p className="mb-1 text-white-50">
              Credential for <strong>{formatValue(loginCred.organization_name)}</strong>
              {loginCred.provider_name ? ` · Provider: ${formatValue(loginCred.provider_name)}` : ""}
            </p>
            <p className="mb-0 text-white-50">Review secure login details and site registration information.</p>
          </div>

          <div className="col-lg-4">
            <div className="p-4 rounded-4" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <span
                  className="d-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 48, height: 48, backgroundColor: "rgba(var(--hc-white-rgb), 0.18)" }}
                >
                  <MdLock size={24} className="text-white" />
                </span>
                <div>
                  <p className="text-white-50 mb-1" style={{ fontSize: "0.85rem" }}>
                    Username
                  </p>
                  <p className="text-white fw-semibold mb-0">{formatValue(loginCred.username)}</p>
                </div>
              </div>
              <div className="text-white-50 small">
                Username and password are stored securely. Password can be revealed after re-authentication.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 align-items-stretch">
        <div className="col-xl-8 d-flex">
          <div className="p-4 p-lg-5 rounded-4 hc-surface hc-card-primary flex-fill h-100">
            <h5 className="fw-bold hc-text-primary mb-4">Credential Information</h5>
            <div className="row g-3 align-items-stretch">
              {[
                { label: "Title", value: formatValue(loginCred.title) },
                { label: "Status", value: formatValue(loginCred.status_name) },
                { label: "Username", value: formatValue(loginCred.username) },
                { label: "Password", value: passwordPlaceholder, isPassword: true },
                { label: "Phone", value: formatValue(loginCred.phone) },
                { label: "Email", value: formatValue(loginCred.email) },
              ].map((item) => (
                <div key={item.label} className="col-md-6 d-flex">
                  <div className="rounded-4 border p-3 h-100 bg-light-subtle d-flex flex-column justify-content-between w-100">
                    <div className="small hc-text-primary fw-semibold mb-1">{item.label}</div>
                    {renderCredentialItemValue(item)}
                  </div>
                </div>
              ))}

              <div className="col-md-6 d-flex">
                <div className="rounded-4 border p-3 h-100 bg-light-subtle d-flex flex-column justify-content-between w-100">
                  <div className="small hc-text-primary fw-semibold mb-1">Site URL</div>
                  {(matchedSiteUrl || loginCred.site_url) ? (
                    <a
                      href={matchedSiteUrl || loginCred.site_url}
                      target="_blank"
                      rel="noreferrer"
                      className="d-block text-primary text-truncate"
                    >
                      {matchedSiteUrl || loginCred.site_url}
                    </a>
                  ) : (
                    <div>{formatValue(matchedSiteUrl || loginCred.site_url)}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6 d-flex">
                <div className="rounded-4 border p-3 h-100 bg-light-subtle d-flex flex-column justify-content-between w-100">
                  <div className="small hc-text-primary fw-semibold mb-1">Registered URL</div>
                  {(matchedRegisteredUrl || loginCred.registered_url) ? (
                    <a
                      href={matchedRegisteredUrl || loginCred.registered_url}
                      target="_blank"
                      rel="noreferrer"
                      className="d-block text-primary text-truncate"
                    >
                      {matchedRegisteredUrl || loginCred.registered_url}
                    </a>
                  ) : (
                    <div>{formatValue(matchedRegisteredUrl || loginCred.registered_url)}</div>
                  )}
                </div>
              </div>

              <div className="col-12 d-flex">
                <div className="rounded-4 border p-3 h-100 bg-light-subtle d-flex flex-column justify-content-between w-100">
                  <div className="small hc-text-primary fw-semibold mb-1">MFA User</div>
                  <div className="fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>{formatValue(loginCred.mfa_user)}</div>
                </div>
              </div>

            </div>
          </div>
        </div>
    
        <div className="col-xl-4 d-flex flex-column gap-1">
          <div className="p-4 p-lg-5 rounded-4 hc-surface hc-card-primary flex-fill mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0 hc-text-primary">Security Details</h5>
            </div>
            <div className="d-flex align-items-start gap-3 mb-3">
              <MdLock size={22} className="text-primary" />
              <div>
                <p className="mb-1 fw-semibold">Re-authentication required</p>
                <p className="text-secondary small mb-0">
                  Password view requires your current password for verification.
                </p>
              </div>
            </div>
            <div className="d-flex align-items-start gap-3">
              <MdOutlinePublic size={22} className="text-primary" />
              <div>
                <p className="mb-1 fw-semibold">External access</p>
                <p className="text-secondary small mb-0">
                  Site and registration links are provided for quick access.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 p-lg-5 rounded-4 hc-surface hc-card-primary flex-fill">
            <h5 className="fw-bold mb-3 hc-text-primary">Backup Codes</h5>
            {hasBackupCodes ? (
              <div className="d-flex flex-column gap-3">
                <div
                  className="rounded-4 bg-light border w-100 px-3 py-2 text-dark text-wrap"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {currentBackupCode}
                </div>
                <div className="d-flex flex-md-row flex-column gap-2 flex-wrap align-items-center justify-content-between mt-2">
                  <button
                    type="button"
                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                    onClick={prevBackupCode}
                    disabled={!hasBackupCodes || backupCodeIndex <= 0}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                    onClick={nextBackupCode}
                    disabled={!hasBackupCodes || backupCodeIndex >= backupCodes.length - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-secondary small mb-0">No backup codes have been added.</p>
            )}
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4 mt-2">
        <div className="col-12">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary">
            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 hc-text-primary">
              <MdPerson size={22} className="hc-text-primary" /> Audit Info
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdPerson size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small hc-text-primary fw-semibold mb-1">Created By</div>
                    <div className="fw-medium text-dark">{createdBy}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdCalendarMonth size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small hc-text-primary fw-semibold mb-1">Created On</div>
                    <div className="fw-medium text-dark">{createdOnLocal || "-"}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdPerson size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small hc-text-primary fw-semibold mb-1">Modified By</div>
                    <div className="fw-medium text-dark">{modifiedBy}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdCalendarMonth size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small hc-text-primary fw-semibold mb-1">Modified On</div>
                    <div className="fw-medium text-dark">{modifiedOnLocal || "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReAuthModal
        isOpen={showReAuthModal}
        title="Re-authenticate"
        description="Enter your password to view this credential."
        password={reauthPassword}
        onPasswordChange={(value) => {
          setReauthPassword(value);
          setReauthError("");
        }}
        showPassword={showReauthPassword}
        onTogglePassword={() => setShowReauthPassword((value) => !value)}
        error={reauthError}
        loading={reauthLoading}
        attemptsRemaining={Math.max(0, MAX_REAUTH_ATTEMPTS - reauthAttempts)}
        maxAttempts={MAX_REAUTH_ATTEMPTS}
        onCancel={() => setShowReAuthModal(false)}
        onConfirm={handleReAuth}
      />
    </div>
  );
}